import React, { useEffect, useRef, useState } from "react";
import ApiService from "../../api/apiService";
import showSwal from "../swal";
import { common } from "../../util/common";
import ApprovalItemList from "./reusable/approval-item-list.jsx";
import FormsSearch from "./reusable/forms-search.jsx";
import "./request-main.css";
import FormsDropdown from "./reusable/forms-search-dropdown.jsx";

function ApprovalRequests() {
  const FETCH_LIMIT = 100;
  const MAX_PAGES = 50;

  const [formData, setFormData] = useState([]);
  const [isInitialLoading, setIsInitialLoading] =
    useState(true);
  const [formsList, setFormsList] = useState([]);
  const [selectedForm, setSelectedForm] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");

  const debounceRef = useRef(null);

  /*
   * Fetch CCAR details because the approval-list API
   * does not contain warehouse and reference_number.
   */
  const enrichRequestsWithCcarDetails = async (
    requests = [],
  ) => {
    const enrichedRequests = await Promise.all(
      requests.map(async (item) => {
        const isCcar =
          item?.request_type?.toUpperCase() === "CCAR";

        if (!isCcar || !item?.id) {
          return item;
        }

        try {
          /*
           * This is the same API method used by
           * your view-request-detail component.
           */
          const detailResponse =
            await ApiService.forms.getFormRequestDetail(
              item.request_type,
              item.id,
            );

          console.log(
            "CCAR DETAIL RESPONSE:",
            detailResponse,
          );

          if (!detailResponse?.success) {
            return item;
          }

          /*
           * Your screenshot shows:
           *
           * detailResponse.request.warehouse
           * detailResponse.request.reference_number
           *
           * The additional fallbacks support a nested response
           * in case the backend changes the structure.
           */
          const responseRequest =
            detailResponse?.request || null;

          const ccarRequest =
            responseRequest?.warehouse !== undefined ||
            responseRequest?.reference_number !== undefined
              ? responseRequest
              : responseRequest?.requisition
                  ?.form_request_detail ||
                responseRequest?.form_request_detail ||
                null;

          return {
            ...item,
            ccar_request: ccarRequest,
          };
        } catch (error) {
          console.error(
            `Failed to fetch CCAR details for ${item.id}:`,
            error,
          );

          return item;
        }
      }),
    );

    return enrichedRequests;
  };

  /*
   * Fetch every page of pending approvals so the full list
   * is visible without pagination. Stops when a page returns
   * no new items (also guards against the backend ignoring
   * the page/limit params).
   */
  const getForms = async ({ keyword = "" } = {}) => {
    try {
      setIsInitialLoading(true);

      const allRequests = [];
      const seenIds = new Set();

      for (let page = 1; page <= MAX_PAGES; page++) {
        const response =
          await ApiService.forms.getApprovalRequestsListV2(
            page,
            FETCH_LIMIT,
            keyword,
            selectedForm,
          );

        const { success, requests = [] } =
          response || {};

        if (!success) {
          showSwal(
            common.errors.GETTING_APPROVAL_LIST_ERROR,
          );
          break;
        }

        const newRequests = requests.filter(
          (item) => !seenIds.has(item?.id),
        );

        if (newRequests.length === 0) {
          break;
        }

        newRequests.forEach((item) =>
          seenIds.add(item?.id),
        );
        allRequests.push(...newRequests);
      }

      /*
       * Load warehouse and reference number
       * for all CCAR requests.
       */
      const enrichedRequests =
        await enrichRequestsWithCcarDetails(allRequests);

      setFormData(enrichedRequests);
    } catch (error) {
      console.error(
        "Failed to fetch approval requests:",
        error,
      );

      showSwal(
        common.errors.GETTING_APPROVAL_LIST_ERROR,
      );
    } finally {
      setIsInitialLoading(false);
    }
  };

  const getFormsList = async () => {
    try {
      const response =
        await ApiService.forms.getForms();

      const { success, forms = [] } =
        response || {};

      if (success) {
        const formattedForms = forms.map(
          (form) => ({
            label: form.name,
            value: form.id,
          }),
        );

        setFormsList(formattedForms);
      } else {
        showSwal(
          common.errors.GETTING_FORMS_LIST_ERROR,
        );
      }
    } catch (error) {
      console.error(
        "Failed to fetch forms list:",
        error,
      );
    }
  };

  useEffect(() => {
    getFormsList();
  }, []);

  /*
   * Runs on mount and whenever the form filter changes.
   */
  useEffect(() => {
    setFormData([]);

    getForms({ keyword: searchKeyword });
  }, [selectedForm]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const handleSearch = (query) => {
    const trimmedQuery = query.trim();

    setSearchKeyword(trimmedQuery);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      setFormData([]);

      getForms({ keyword: trimmedQuery });
    }, 400);
  };

  return (
    <div className="request-main">
      <div className="request-main-title">
        <div className="request-main-title__top">
          <h3>My Pending Approvals</h3>
        </div>

        <div className="request-main-title__search">
          <FormsSearch
            handleSearch={handleSearch}
          />

          <FormsDropdown
            value={selectedForm}
            options={formsList}
            handleSelect={(value) =>
              setSelectedForm(value)
            }
          />
        </div>
      </div>

      {isInitialLoading ? (
        <div className="loader--container">
          <div className="loader--"></div>
        </div>
      ) : (
        <div
          id="approval-requests-scrollable-container"
          className="request-main---container"
        >
          {formData.length > 0 ? (
            <div className="request-main__list">
              {formData.map((data) => (
                <div
                  className="request-main__item"
                  key={data?.id}
                >
                  <ApprovalItemList
                    data={data}
                  />
                </div>
              ))}

              <p className="request-main__end-message">
                You’ve reached the very end of the list.
              </p>
            </div>
          ) : (
            <div className="request-main__empty-state">
              <div className="request-main__empty-icon">
                ✅
              </div>

              <h4>
                No pending approvals found
              </h4>

              <p>
                {searchKeyword
                  ? "Try changing your search keyword."
                  : "You currently have no pending approvals."}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ApprovalRequests;